import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box } from '@mui/material';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import React from 'react';

import { linkOptions } from '../../routing/links/linkOptions';
import { colorSystem } from '../../theme';
import { BackButton, PrimaryButton } from '../buttons';
import { CheckableChip } from '../Chip';
import { Flexbox } from '../Flexbox';
import { Link } from '../Link';
import { Text } from '../Text';
import { HelpDialog } from './components/HelpDialog';
import { StepHelpDialog } from './components/StepHelpDialog';
import { Step, StepperProps } from './types';

export function Stepper({
    title = '',
    stepIndex,
    hideControls,
    steps,
    onStepIndexChange = () => {},
    onSubmit,
    back,
    labelSubmitButton = t`Import`,
    tools,
    ...props
}: StepperProps): JSX.Element {
    const step = steps[stepIndex];
    const [showHelpDialog, setShowHelpDialog] = React.useState(false);
    const { isPending, mutateAsync } = usePromise(onSubmit);

    const isLastStep = stepIndex === steps.length - 1;
    const isNextStepDisabled = !isLastStep && !steps[stepIndex + 1].enabled;

    const nextButton = (
        <PrimaryButton
            sx={{ justifySelf: 'flex-end' }}
            disabled={isNextStepDisabled}
            disableFocusRipple
            autoFocus={!isNextStepDisabled}
            size="medium"
            onClick={() => onStepIndexChange('next')}
        >
            <Trans>Next</Trans>
        </PrimaryButton>
    );

    const submitButton = (
        <PrimaryButton
            isLoading={isPending}
            sx={{ justifySelf: 'flex-end' }}
            startIcon={<CheckIcon />}
            disabled={!step.isDone}
            size="medium"
            onClick={() => mutateAsync()}
        >
            {labelSubmitButton}
        </PrimaryButton>
    );

    return (
        <Flexbox
            {...props}
            sx={[
                {
                    position: 'relative',
                    flexDirection: 'column',
                    height: '100vh',
                    width: '100vw',
                    boxSizing: 'border-box',
                    bgcolor: colorSystem.neutral.white,
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            <Box
                sx={{
                    display: hideControls ? 'none' : 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    padding: '8px',
                    gap: '8px',
                    alignItems: 'center',
                    justifyItems: 'end',
                    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
                }}
            >
                <Box sx={{ alignItems: 'center', justifySelf: 'flex-start', gap: 1, display: 'flex' }}>
                    <BackButton fallback={back ?? linkOptions({ to: '/' })} />
                    <Text variant="h3">{title}</Text>
                </Box>
                <StepperNavigation onStepIndexChange={onStepIndexChange} stepIndex={stepIndex} steps={steps} />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    {step.helpButton && (
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Link
                                component="button"
                                attention="high"
                                variant="body-small"
                                onClick={() => {
                                    setShowHelpDialog(true);
                                }}
                            >
                                {step.helpButton.buttonText}
                            </Link>
                        </span>
                    )}
                    {tools}
                    {!isLastStep ? nextButton : submitButton}
                </div>
            </Box>
            <Flexbox
                sx={{
                    flexGrow: 1,
                    maxHeight: '100vh',
                    overflow: 'auto',
                }}
            >
                <StepView step={step} steps={steps} onStepIndexChange={onStepIndexChange} stepIndex={stepIndex} />
            </Flexbox>
            <HelpDialog
                title={step.helpButton?.title ?? ''}
                body={step.helpButton?.body ?? <></>}
                width={'md'}
                showDialog={showHelpDialog}
                onClose={() => {
                    setShowHelpDialog(false);
                }}
            />
        </Flexbox>
    );
}

function usePromise<T>(fn?: () => Promise<T>): {
    isPending: boolean;
    mutateAsync: () => Promise<void>;
} {
    const [isPending, setIsPending] = React.useState(false);

    const mutateAsync = async () => {
        if (!fn) {
            return;
        }
        try {
            setIsPending(true);
            await fn();
            setIsPending(false);
        } finally {
            setIsPending(false);
        }
    };

    return {
        isPending,
        mutateAsync,
    };
}

export function useStepperState(steps: number): {
    stepIndex: number;
    setStepIndex: StepperProps['onStepIndexChange'];
} {
    const [stepIndex, setStepIndex] = React.useState(0);
    return {
        stepIndex,
        setStepIndex: (step: number | 'next' | 'prev') => {
            let numStep = 0;
            if (typeof step === 'number') {
                numStep = step;
            }
            if (step === 'next') {
                numStep = stepIndex + 1;
            }
            if (step === 'prev') {
                numStep = stepIndex - 1;
            }

            if (numStep <= 0) {
                setStepIndex(0);
                return;
            }
            if (numStep >= steps) {
                setStepIndex(steps - 1);
                return;
            }
            setStepIndex(numStep);
        },
    };
}

function StepView({ step, onStepIndexChange }: StepperProps & { step: Step }) {
    const dialog = step.helpDialog ? (
        <StepHelpDialog key={step.label} {...step.helpDialog} storage={step.helpDialog.storage} />
    ) : null;

    if (typeof step.content === 'function') {
        const Component = step.content;
        return (
            <>
                {dialog}
                <Component onStepIndexChange={onStepIndexChange} />
            </>
        );
    }
    return (
        <>
            {dialog}
            {step.content}
        </>
    );
}

function StepperNavigation({ stepIndex, steps, onStepIndexChange = () => {} }: StepperProps) {
    return (
        <Flexbox
            gap={4}
            sx={{
                alignItems: 'center',
                background: colorSystem.neutral.white,
                borderRadius: '64px',
            }}
        >
            {steps.map((step, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <ChevronRightIcon size={24} color={colorSystem.neutral[1]} />}
                    <StepperButton
                        step={step}
                        number={i + 1}
                        onClick={() => onStepIndexChange(i)}
                        isSelected={stepIndex === i}
                    />
                </React.Fragment>
            ))}
        </Flexbox>
    );
}

function StepperButton({
    isSelected,
    number,
    onClick,
    step,
}: {
    isSelected: boolean;
    number: number;
    onClick: () => void;
    step: Step;
}) {
    return (
        <CheckableChip
            onClick={() => onClick()}
            label={step.label ?? ''}
            selected={isSelected}
            count={number}
            done={step.isDone}
            disabled={!step.enabled}
        />
    );
}
